import { Injectable } from '@angular/core';
import { BullhornEntityTypes } from 'datasource-service-lib';
import { Namespace } from 'enums-lib';
import { IDataSourceApiService, IDocumentDestinationConfig, IDocumentDestinationOptions, Section } from 'interfaces-lib';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BullhornSettingsService } from 'settings-lib';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { IDocumentDestinationService } from '../../model/interfaces/idocumentdestinationservice';
import { BullhornEntityFieldMetaService } from '../entity-field-meta/bullhorn.entity-field-meta.service';
import { IDestinationsForOutputFileType } from '../entity-type/entity-type-service.factory';
import { IEntityLabelPair } from '../entity-type/entity-type.service';

@Injectable({ providedIn: 'root' })
export class BullhornDocumentDestinationService implements IDocumentDestinationService {
  private readonly namespace: Namespace;
  documentDestinationOptionsAllowed = true;
  documentDestinationMeta$ = this.bullhornEntityFieldMetaService.entityMeta$;
  loading$ = this.bullhornEntityFieldMetaService.loading$;

  apiService: IDataSourceApiService;

  constructor(
    private identityStorage: IdentityStorage,
    private bullhornEntityFieldMetaService: BullhornEntityFieldMetaService,
    private sharedServiceProviderFactory: SharedServiceProviderServiceFactory,
    private bullhornSettingsService: BullhornSettingsService
  ) {
    this.namespace = this.identityStorage.get().namespace;
    this.apiService = this.sharedServiceProviderFactory.getServiceProviderService(this.namespace).getDataSourceApiService();
  }

  getDocumentDestinationOptions(
    entities: IEntityLabelPair[],
    templateEntityName: string,
    availability: string[] | undefined
  ): Observable<IDocumentDestinationConfig[]> {
    return this.bullhornSettingsService.clientLeadOpportunityEnabled.pipe(
      map(() => {
        const destinationOptions: IDocumentDestinationConfig[] = [];
        availability = availability ?? this.defaultAvailability(templateEntityName);
        for (let i = 0; i < availability.length; i++) {
          destinationOptions.push({
            entityType: availability[i],
            label: this.bullhornEntityFieldMetaService.getMetaLabelFromPath(entities, availability[i]),
            options: this.getOptionsForAvailability(templateEntityName, availability[i], entities),
          });
        }
        return destinationOptions;
      })
    );
  }

  getMetaLabelFromDestinationPath(entities: IEntityLabelPair[], path: string): string | undefined {
    return this.bullhornEntityFieldMetaService.getMetaLabelFromPath(entities, path);
  }

  getEntityTypeFromDestinationPath(path: string): string {
    const type: string = path.split('.').slice(-1).toString().toLowerCase();
    switch (type) {
      case 'candidate':
        return BullhornEntityTypes.Candidate;
      case 'joborder':
        return BullhornEntityTypes.JobOrder;
      case 'clientcontact':
        return BullhornEntityTypes.ClientContact;
      case 'clientcorporation':
      case 'vendorclientcorporation':
        return BullhornEntityTypes.ClientCorporation;
      case 'opportunity':
        return BullhornEntityTypes.Opportunity;
      case 'placement':
        return BullhornEntityTypes.Placement;
      case 'jobsubmission':
        return BullhornEntityTypes.JobSubmission;
      case 'lead':
        return BullhornEntityTypes.Lead;
      default:
        throw new Error('There was an error getting the entity type from the document destination path');
    }
  }

  getDestinationsForOutputFileType(
    entities: IEntityLabelPair[],
    destinationEntityTypes: string[],
    section: Section
  ): IDestinationsForOutputFileType[] {
    if (!destinationEntityTypes.length) {
      return undefined;
    }

    const destinations: IDestinationsForOutputFileType[] = [];
    const labelFromMeta = this.getMetaLabelFromDestinationPath(entities, 'ClientCorporation');
    if (destinationEntityTypes.includes(BullhornEntityTypes.ClientCorporation)) {
      destinations.push({
        type: labelFromMeta,
        documentType: this.getDocumentTypeFromForm('ClientCorporation', section),
        entityTypes: [BullhornEntityTypes.ClientCorporation],
      });
    }

    const nonClientCorpDestinations = destinationEntityTypes.filter((x) => x !== BullhornEntityTypes.ClientCorporation);
    if (nonClientCorpDestinations.length > 0) {
      destinations.push({
        type: `Non-${labelFromMeta}`,
        documentType: this.getDocumentTypeFromForm('Non-ClientCorporation', section),
        entityTypes: nonClientCorpDestinations,
      });
    }

    return destinations;
  }

  private sortOptions(entityType: string, options: IDocumentDestinationOptions[]): IDocumentDestinationOptions[] {
    return options.sort(function (a, b) {
      if (entityType !== BullhornEntityTypes.JobSubmission) {
        if (a.path === b.path) {
          return 0;
        }
        if (a.path.split('.').length === 1) {
          return -1;
        }
        if (b.path.split('.').length === 1) {
          return 1;
        }
      }

      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
  }

  private defaultAvailability(templateEntityName: string): string[] {
    if (templateEntityName === BullhornEntityTypes.JobSubmission) {
      return [BullhornEntityTypes.JobOrder];
    }

    return [templateEntityName];
  }

  private getDocumentTypeFromForm(type: string, section: Section): string | undefined {
    // legacy templates
    if (section.sourceValue) {
      return section.sourceValue as string;
    }

    if (section.config?.destinations) {
      switch (type) {
        case 'ClientCorporation':
          return section.config?.destinations[type] ?? undefined;
        case 'Non-ClientCorporation':
          const key = Object.keys(section.config?.destinations).filter((x) => x !== 'ClientCorporation')[0];
          return section.config?.destinations[key] ?? undefined;
        default:
          return undefined;
      }
    }
    return undefined;
  }

  private getOptionsForAvailability(
    templateEntityName: string,
    availabilityItem: string,
    entities: IEntityLabelPair[]
  ): IDocumentDestinationOptions[] {
    const options = bullhornDocumentDestinationOptions[templateEntityName];

    const filteredOptions = options
      .filter((option) => this.shouldIncludeOption(templateEntityName, option))
      .filter((option) => option.availability === availabilityItem)
      .map((option) => ({
        ...option,
        label: this.bullhornEntityFieldMetaService.getMetaLabelFromPath(entities, option.path),
      }));

    return this.sortOptions(templateEntityName, filteredOptions);
  }

  private shouldIncludeOption(templateEntityName: string, option: IDocumentDestinationOptions): boolean {
    const isJobOrder = templateEntityName === BullhornEntityTypes.JobOrder;
    const includesOpportunity = option.path.includes('opportunity');

    // Exclude Opportunity option if current entity is JobOrder, and the client have that disabled
    return !(isJobOrder && includesOpportunity && !this.bullhornEntityFieldMetaService.isClientLeadOpportunityEnabled);
  }
}

const bullhornDocumentDestinationOptions: {
  [entity: string]: IDocumentDestinationOptions[];
} = {
  Candidate: [
    {
      path: 'Candidate',
      default: true,
      availability: BullhornEntityTypes.Candidate,
    },
    {
      path: 'Candidate',
      default: true,
      availability: BullhornEntityTypes.JobOrder,
    },
    {
      path: 'Candidate.jobSubmission.jobOrder',
      default: true,
      availability: BullhornEntityTypes.JobOrder,
    },
  ],
  ClientContact: [
    {
      path: 'ClientContact',
      default: true,
      availability: BullhornEntityTypes.ClientContact,
    },
    {
      path: 'ClientContact',
      default: false,
      availability: BullhornEntityTypes.ClientCorporation,
    },
    {
      path: 'ClientContact.clientCorporation',
      default: false,
      availability: BullhornEntityTypes.ClientContact,
    },
    {
      path: 'ClientContact.clientCorporation',
      default: true,
      availability: BullhornEntityTypes.ClientCorporation,
    },
  ],
  JobOrder: [
    {
      path: 'JobOrder',
      default: true,
      availability: BullhornEntityTypes.JobOrder,
    },
    {
      path: 'JobOrder.clientContact',
      default: false,
      availability: BullhornEntityTypes.JobOrder,
    },
    {
      path: 'JobOrder.clientCorporation',
      default: false,
      availability: BullhornEntityTypes.JobOrder,
    },
    {
      path: 'JobOrder.opportunity',
      default: false,
      availability: BullhornEntityTypes.JobOrder,
    },
  ],
  ClientCorporation: [
    {
      path: 'ClientCorporation',
      default: true,
      availability: BullhornEntityTypes.ClientCorporation,
    },
  ],
  Placement: [
    // As per AC on KAD2-1455 - "Whenever a document is saved to a Placement, Bullhorn will then save it to the associated JobOrder too"
    // So we only include a Placement option, but add a label to this option showing that it will save to both entities.
    {
      path: 'Placement',
      default: true,
      availability: BullhornEntityTypes.Placement,
    },
    {
      path: 'Placement.candidate',
      default: false,
      availability: BullhornEntityTypes.Placement,
    },
    {
      path: 'Placement.clientCorporation',
      default: false,
      availability: BullhornEntityTypes.Placement,
    },
    {
      path: 'Placement.vendorClientCorporation',
      default: false,
      availability: BullhornEntityTypes.Placement,
    },
    {
      path: 'Placement.jobOrder',
      default: false,
      availability: BullhornEntityTypes.Placement,
    },
    {
      path: 'Placement.jobOrder.clientContact',
      default: false,
      availability: BullhornEntityTypes.Placement,
    },
  ],
  JobSubmission: [
    {
      path: 'JobSubmission.jobOrder',
      default: true,
      availability: BullhornEntityTypes.JobOrder,
    },
    {
      path: 'JobSubmission.candidate',
      default: true,
      availability: BullhornEntityTypes.JobOrder,
    },
    {
      path: 'JobSubmission.jobOrder.clientContact',
      default: false,
      availability: BullhornEntityTypes.JobOrder,
    },
    {
      path: 'JobSubmission.jobOrder.clientCorporation',
      default: false,
      availability: BullhornEntityTypes.JobOrder,
    },
  ],
  Opportunity: [
    {
      path: 'Opportunity',
      default: true,
      availability: BullhornEntityTypes.Opportunity,
    },
    {
      path: 'Opportunity.clientContact',
      default: false,
      availability: BullhornEntityTypes.Opportunity,
    },
    {
      path: 'Opportunity.clientCorporation',
      default: false,
      availability: BullhornEntityTypes.Opportunity,
    },
  ],
};
